// Single source of truth for the Technical Audit site health score (GH #596).
//
// The old formula subtracted a FLAT penalty per issue with NO normalization for site
// size — `100 - (critical*5 + warning*2 + info*0.5)` clamped to [0,100] — so any real
// multi-page site floored to 0/100 instantly (e.g. instantwaterheaters.com: penalty
// ~2,774 against a 100-point scale). Every site read "0/100 health", which is useless.
//
// This normalizes the severity-weighted penalty by the number of pages crawled, so the
// score reflects issue DENSITY (how bad per page) rather than raw count: a large site
// with a few issues per page scores well; a small site riddled with them scores low.
// Bounded to 0..100 and — for any run with a real page count — never floors.
//
// Tunables: `W_*` weight the severities (critical hurts most, info barely counts);
// `PENALTY_CEILING_PER_PAGE` is the weighted issues PER PAGE at which health reaches 0.
const W_CRITICAL = 3;
const W_WARNING = 1;
const W_INFO = 0.25;
const PENALTY_CEILING_PER_PAGE = 5;

type SeverityCounts = {
  critical?: number;
  warning?: number;
  info?: number;
};

export function computeHealthScore(
  bySeverity: SeverityCounts | Record<string, number> | undefined,
  pagesCrawled?: number,
): number {
  const c = bySeverity?.critical ?? 0;
  const w = bySeverity?.warning ?? 0;
  const i = bySeverity?.info ?? 0;
  const weighted = c * W_CRITICAL + w * W_WARNING + i * W_INFO;
  // Divide by the real page count when we have one. Only when it's missing
  // (legacy/partial data) fall back to the issue count as a rough page proxy — that
  // degrades gently instead of dividing by ~1 and re-flooring the score.
  const pages =
    pagesCrawled && pagesCrawled > 0
      ? pagesCrawled
      : Math.max(c + w + i, 1);
  const perPage = weighted / pages;
  const score = 100 * (1 - Math.min(1, perPage / PENALTY_CEILING_PER_PAGE));
  return Math.max(0, Math.min(100, Math.round(score)));
}
